import { useEffect, useRef, useSyncExternalStore } from "react";
import { useTranslation } from "react-i18next";
import { currentConfirmRequest, registerConfirmHost, settleConfirmRequest, subscribeConfirmQueue } from "./confirmDialog";
import { HOS_SHELL_ENABLED } from "./hos/shell/hosShellFlag";
import "../styles/hos/components/button.css";
import "../styles/hos/components/overlays.css";

// Renders confirmDialog() requests (confirmDialog.ts). Mounted once in App.tsx; the buttons
// reuse the shared PMS action labels in every language. With the new shell the dialog uses the
// HOS look (DESIGN.md §6: title, short text, Cancel secondary + Confirm primary); otherwise the
// legacy markup of PMS.GroupConfirmDialog (rsvCancel* classes).
export function ConfirmDialogHost() {
  const { t } = useTranslation();
  const request = useSyncExternalStore(subscribeConfirmQueue, currentConfirmRequest, currentConfirmRequest);
  const cancelRef = useRef<HTMLButtonElement | null>(null);
  const confirmRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => registerConfirmHost(), []);

  useEffect(() => {
    if (!request) return undefined;
    const previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    cancelRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") settleConfirmRequest(false);
      // Two buttons only: Tab keeps cycling between them instead of leaving the dialog.
      if (event.key === "Tab" && cancelRef.current && confirmRef.current) {
        const next = document.activeElement === cancelRef.current ? confirmRef.current : cancelRef.current;
        event.preventDefault();
        next.focus();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      if (previouslyFocused && document.contains(previouslyFocused)) previouslyFocused.focus({ preventScroll: true });
    };
  }, [request]);

  if (!request) return null;
  const paragraphs = request.message.split("\n").map((line) => line.trim()).filter(Boolean);
  const titleId = `app-confirm-title-${request.id}`;

  if (HOS_SHELL_ENABLED) {
    const [title, ...lines] = paragraphs;
    return (
      <div className="hos-ui hos-ov-modal" role="alertdialog" aria-modal="true" aria-labelledby={titleId}>
        <div className="hos-ov-modal__scrim" aria-hidden="true" />
        <div className="hos-ov-modal__card">
          <h2 className="hos-ov-modal__title" id={titleId}>{title ?? ""}</h2>
          {lines.map((line, index) => (
            <p className="hos-ov-modal__text" key={`${request.id}-${index}`}>{line}</p>
          ))}
          <div className="hos-ov-modal__actions">
            <button type="button" className="hos-btn hos-btn--secondary" onClick={() => settleConfirmRequest(false)} ref={cancelRef}>
              {t("pms.common.actions.cancel")}
            </button>
            <button type="button" className="hos-btn hos-btn--primary" onClick={() => settleConfirmRequest(true)} ref={confirmRef}>
              {t("pms.common.actions.confirm")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rsvCancelOverlay appConfirmOverlay" role="alertdialog" aria-modal="true" aria-label={paragraphs[0] ?? ""}>
      <div className="rsvCancelModal pmsGroupConfirmModal">
        <div className="rsvCancelBody">
          {paragraphs.map((line, index) => (
            <p className={index === 0 ? "rsvCancelTitle" : "rsvCancelText"} key={`${request.id}-${index}`}>{line}</p>
          ))}
        </div>
        <div className="rsvCancelFooter">
          <button type="button" className="rsvTblCancelBtn" onClick={() => settleConfirmRequest(false)} ref={cancelRef}>
            {t("pms.common.actions.cancel")}
          </button>
          <button type="button" className="rsvCancelConfirmBtn" onClick={() => settleConfirmRequest(true)} ref={confirmRef}>
            {t("pms.common.actions.confirm")}
          </button>
        </div>
      </div>
    </div>
  );
}
